import React, { useCallback, useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { SlideTitle } from '../components/SlideTitle';
import { SlideChallenges } from '../components/SlideChallenges';
import { SlideOverview } from '../components/SlideOverview';
import { SlideBeforeCongress } from '../components/SlideBeforeCongress';
import { SlideArrival } from '../components/SlideArrival';
import { SlideAisleToBooth } from '../components/SlideAisleToBooth';
import { SlideInsideBooth } from '../components/SlideInsideBooth';
import { SlidePostCongress } from '../components/SlidePostCongress';
import { SlideResults } from '../components/SlideResults';
import { SlideClosing } from '../components/SlideClosing';
const slides = [SlideTitle, SlideChallenges, SlideOverview, SlideBeforeCongress, SlideArrival, SlideAisleToBooth, SlideInsideBooth, SlidePostCongress, SlideResults, SlideClosing];
export function Presentation() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(0);
  const nextSlide = useCallback(() => {
    if (currentSlide < slides.length - 1) {
      setDirection(1);
      setCurrentSlide(prev => prev + 1);
    }
  }, [currentSlide]);
  const prevSlide = useCallback(() => {
    if (currentSlide > 0) {
      setDirection(-1);
      setCurrentSlide(prev => prev - 1);
    }
  }, [currentSlide]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Space') {
        nextSlide();
      } else if (e.key === 'ArrowLeft') {
        prevSlide();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);
  const CurrentSlideComponent = slides[currentSlide];
  return <div className="relative w-full h-screen overflow-hidden bg-black" data-id="element-731">
      <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-732">
        <motion.div key={currentSlide} custom={direction} variants={{
        enter: (direction: number) => ({
          x: direction > 0 ? 1000 : -1000,
          opacity: 0
        }),
        center: {
          zIndex: 1,
          x: 0,
          opacity: 1
        },
        exit: (direction: number) => ({
          zIndex: 0,
          x: direction < 0 ? 1000 : -1000,
          opacity: 0
        })
      }} initial="enter" animate="center" exit="exit" transition={{
        x: {
          type: 'spring',
          stiffness: 300,
          damping: 30
        },
        opacity: {
          duration: 0.2
        }
      }} className="absolute inset-0 w-full h-full" data-id="element-733">
          <CurrentSlideComponent data-id="element-734" />
        </motion.div>
      </AnimatePresence>

      {/* Logos — persistent across all slides */}
      <div className="absolute top-5 right-20 z-50 flex items-center gap-3" data-id="element-735">
        <div className="bg-white rounded-full px-3 py-1.5 shadow-md" data-id="element-736">
          <img src="https://cdn.magicpatterns.com/uploads/6UmWqd3TWdNZzkJ6AZQjM5/image.png" alt="Snakker logo" className="h-5 w-auto object-contain" data-id="element-737" />
        </div>
        <div className="bg-white rounded-full px-3 py-1.5 shadow-md" data-id="element-738">
          <img src="https://cdn.magicpatterns.com/uploads/1yzsnj6wqDNUHRV1oyVyfh/Screenshot_2026-03-24_at_08.34.05.png" alt="Takeda logo" className="h-6 w-auto object-contain" data-id="element-739" />
        </div>
      </div>

      {/* Navigation Controls */}
      <div className="absolute bottom-6 left-0 w-full px-8 flex justify-between items-center z-50 pointer-events-none" data-id="element-740">
        <button onClick={prevSlide} disabled={currentSlide === 0} className={`
            pointer-events-auto p-2 rounded-full bg-black/10 hover:bg-black/20 text-gray-500 hover:text-gray-900 transition-all
            ${currentSlide === 0 ? 'opacity-0' : 'opacity-100'}
          `} aria-label="Previous slide" data-id="element-741">
          <ChevronLeft className="w-8 h-8" data-id="element-742" />
        </button>

        <span className="text-sm font-medium text-gray-400 bg-white/80 px-3 py-1 rounded-full backdrop-blur-sm shadow-sm" data-id="element-743">
          {currentSlide + 1} / {slides.length}
        </span>

        <button onClick={nextSlide} disabled={currentSlide === slides.length - 1} className={`
            pointer-events-auto p-2 rounded-full bg-black/10 hover:bg-black/20 text-gray-500 hover:text-gray-900 transition-all
            ${currentSlide === slides.length - 1 ? 'opacity-0' : 'opacity-100'}
          `} aria-label="Next slide" data-id="element-744">
          <ChevronRight className="w-8 h-8" data-id="element-745" />
        </button>
      </div>
    </div>;
}